import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import SearchIcon from "@mui/icons-material/Search";
import {
  Typography,
  Stack,
  Box,
  TextField,
  InputAdornment,
  Chip,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button
} from "@mui/material";
import {
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row,
  formatDateTime
} from "@qubit/autoparts";
import { t } from "i18next";
import { useState, useMemo, Fragment, useCallback } from "react";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { isHandToggled } from "~/features/andon/andon.logic";

import { selectEventHistory } from "~/features/andon/andon.selectors";
import {
  saveEventLimitData,
  selectAndonEventSaveLimit,
  clearEventData
} from "~/features/andon/andon.slice";
import { AndonEventWithMeta } from "~/features/andon/andon.types";
import {
  mapEventColorAndLetters,
  mapEventStatus,
  getEventStatusColor,
  getHubColor
} from "~/features/andon/andon.utils";
import { getTimeFromDatetime } from "~/lib/helpers";
import { workstationApi } from "~/redux/warehouse/workstation";

import EventsDrawer from "./EventsDrawer";

export type EventSortField =
  | "timestamp"
  | "hub"
  | "eventType"
  | "workstation"
  | "port"
  | "bin"
  | "status"
  | "details";

const EVENT_SAVE_LIMIT_OPTIONS = [0, 50, 100, 250, 500, 1000];

export function EventsTable() {
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedEventIndex, setSelectedEventIndex] = useState<number | null>(
    null
  );
  const [sortField, setSortField] = useState<EventSortField>("timestamp");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const eventHistory = useAppSelector(selectEventHistory);
  const eventSaveLimit = useAppSelector(selectAndonEventSaveLimit);
  const { data: workstations } = workstationApi.useGetWorkstationsQuery();

  const handleHeaderClick = useCallback(
    (field: EventSortField) => {
      if (sortField === field) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
      setCurrentPage(1);
    },
    [sortField, sortDirection]
  );

  // Helper function to get workstation device name from workstation ID
  const getWorkstationDeviceName = useCallback(
    (workstationId: string) => {
      if (!workstations || !workstationId) return workstationId || "—";
      const workstation = workstations.find((ws) => ws.id === workstationId);
      return workstation?.deviceId || workstationId;
    },
    [workstations]
  );

  // Helper function to determine text color for event type bubble
  const getEventBubbleTextColor = (backgroundColor: string) => {
    // Light colors that need dark text for better readability
    const lightColors = [
      "#ffc043", // yellow
      "white",
      "gray.light",
      "#ffffff",
      "#90EE90", // light green
      "#FFFF99", // light yellow variants
      "#FFFFCC",
      "#CCFFCC", // light green variants
      "#E6FFE6"
    ];
    return lightColors.includes(backgroundColor) ? "black" : "white";
  };

  // Helper function to get hub color using the utility function
  const getHubColorStyles = useCallback((hub: string) => {
    const backgroundColor = getHubColor(hub);

    // Return compatible structure with background, border, and text colors
    return {
      backgroundColor: backgroundColor + "20", // Add transparency
      borderColor: backgroundColor,
      textColor: backgroundColor
    };
  }, []);

  const getEventTypeDisplay = (event: AndonEventWithMeta) => {
    const { event: payload } = event;

    if ("eventType" in payload && payload.eventType) {
      return payload.eventType;
    }

    if ("case" in payload && payload.case) {
      return payload.case;
    }

    return "Unknown";
  };

  const getEventDetails = (event: AndonEventWithMeta) => {
    const { event: payload } = event;

    const details: string[] = [];

    if (isHandToggled(payload)) {
      details.push(`Raised: ${payload.event.raiseHand}`);
    } else if ("case" in payload && payload.case) {
      switch (payload.case) {
        case "PortMode":
          details.push(`Mode: ${payload.event.portMode}`);
          break;
        case "PortError":
          details.push(`Error: ${payload.event.hasError}`);
          break;
        case "BinModeChange":
          details.push(`Bin Mode: ${payload.event.binMode}`);
          break;
        case "HandToggled":
          details.push(`Raised: ${payload.event.raiseHand}`);
          break;
        case "SystemModeChanged":
          details.push(`System Mode: ${payload.event.systemMode}`);
          break;
        default:
          if ("event" in payload && typeof payload.event === "object") {
            Object.entries(payload.event).forEach(([key, value]) => {
              if (key !== "gridId" && key !== "portId" && value !== undefined) {
                details.push(`${key}: ${value}`);
              }
            });
          }
      }
    } else if ("eventType" in payload) {
      if ("batchId" in payload && payload.batchId) {
        details.push(`Batch: ${payload.batchId}`);
      }
      if ("tote" in payload && payload.tote) {
        details.push(`Tote: ${payload.tote.toteNumber || payload.tote.toteId}`);
      }
    }

    return details.join(", ") || "—";
  };

  // Sort events by selected field and direction
  const sortedEvents = useMemo(() => {
    return [...eventHistory].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "timestamp":
          comparison =
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
          break;
        case "hub":
          comparison = (a.hub || "").localeCompare(b.hub || "");
          break;
        case "eventType":
          comparison = getEventTypeDisplay(a).localeCompare(
            getEventTypeDisplay(b)
          );
          break;
        case "workstation": {
          const aName = getWorkstationDeviceName(a.workstationId || "");
          const bName = getWorkstationDeviceName(b.workstationId || "");
          comparison = aName.localeCompare(bName);
          break;
        }
        case "port":
          comparison = (a.portId || "").localeCompare(b.portId || "");
          break;
        case "bin":
          comparison = (a.binId || "").localeCompare(b.binId || "");
          break;
        case "status":
          comparison = mapEventStatus(a).localeCompare(mapEventStatus(b));
          break;
        case "details":
          comparison = getEventDetails(a).localeCompare(getEventDetails(b));
          break;
        default:
          comparison =
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [eventHistory, sortField, sortDirection, getWorkstationDeviceName]);

  // Filter events based on search query
  const filteredEvents = useMemo(() => {
    if (!searchQuery.trim()) return sortedEvents;

    const query = searchQuery.toLowerCase();
    return sortedEvents.filter((event) => {
      // Search in multiple fields
      const deviceName = getWorkstationDeviceName(event.workstationId || "");
      const searchableText = [
        event.hub,
        event.workstationId || "",
        deviceName,
        event.portId || "",
        event.gridId || "",
        event.binId || "",
        event.status || "",
        JSON.stringify(event.event).toLowerCase(),
        formatDateTime(event.timestamp).toLowerCase()
      ]
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [sortedEvents, searchQuery, getWorkstationDeviceName]);

  // Pagination logic
  const totalPages = Math.ceil(filteredEvents.length / pageSize);
  const paginatedEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredEvents.slice(startIndex, endIndex);
  }, [filteredEvents, currentPage, pageSize]);

  // Reset to page 1 when search query changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const getStatusColor = (event: AndonEventWithMeta) => {
    const { color } = mapEventColorAndLetters(event);
    return color;
  };

  return (
    <Stack spacing={2} height="100%" width="100%">
      {/* Controls and pagination */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mt: 2, flexWrap: "wrap" }}
      >
        {/* Left: save limit, clear, search */}
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ fontSize: ".8rem" }}>
              {t("save limit")}
            </InputLabel>
            <Select
              value={eventSaveLimit}
              label={t("save limit")}
              onChange={(e) => {
                dispatch(saveEventLimitData(Number(e.target.value)));
              }}
            >
              {EVENT_SAVE_LIMIT_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt === 0 ? t("off") : opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="secondary"
            disabled={eventHistory.length === 0}
            onClick={() => dispatch(clearEventData())}
            sx={{ whiteSpace: "nowrap" }}
          >
            {t("clear")}
          </Button>

          <Box sx={{ minWidth: 300 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("search events...")}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }
              }}
            />
          </Box>

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            {filteredEvents.length} of {eventHistory.length} events (page{" "}
            {currentPage} of {totalPages})
          </Typography>
        </Stack>

        {/* Right: rows per page + pagination */}
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>{t("rows per page")}</InputLabel>
            <Select
              value={pageSize}
              label={t("rows per page")}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={250}>250</MenuItem>
            </Select>
          </FormControl>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_event, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      </Stack>

      {/* Events table */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          pb: 2
        }}
      >
        <ASTableV2
          containerSx={{ maxHeight: "80vh" }}
          gridTemplateColumns="minmax(150px, 1fr) minmax(100px, 1fr) minmax(120px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(200px, 2fr)"
          isLoading={false}
          stickyHeader
        >
          <ASTableV2Header>
            <ASTableV2Row>
              {(
                [
                  { field: "timestamp", label: t("timestamp") },
                  { field: "hub", label: t("hub") },
                  { field: "eventType", label: t("event type") },
                  { field: "workstation", label: t("workstation") },
                  { field: "port", label: t("port") },
                  { field: "bin", label: t("bin") },
                  { field: "status", label: t("status") },
                  { field: "details", label: t("details") }
                ] as { field: EventSortField; label: string }[]
              ).map(({ field, label }) => (
                <ASTableV2Cell
                  key={field}
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { bgcolor: "action.hover" }
                  }}
                  onClick={() => handleHeaderClick(field)}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    <span>{label}</span>
                    {sortField === field &&
                      (sortDirection === "asc" ? (
                        <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                      ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                      ))}
                  </Stack>
                </ASTableV2Cell>
              ))}
            </ASTableV2Row>
          </ASTableV2Header>

          <ASTableV2Body>
            {paginatedEvents.map((event, index) => {
              const timeObject = getTimeFromDatetime(event.timestamp);
              const eventType = getEventTypeDisplay(event);
              const eventDetails = getEventDetails(event);
              const statusColor = getStatusColor(event);
              const { letters } = mapEventColorAndLetters(event);

              return (
                <Fragment
                  key={`event-${index}-${new Date(event.timestamp).getTime()}`}
                >
                  <ASTableV2Row
                    selected={selectedEventIndex === index}
                    onClick={() => setSelectedEventIndex(index)}
                    sx={{
                      cursor: "pointer"
                    }}
                  >
                    {/* Timestamp */}
                    <ASTableV2Cell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDateTime(event.timestamp)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          .{String(timeObject.milliseconds)}ms
                        </Typography>
                      </Stack>
                    </ASTableV2Cell>

                    {/* Hub */}
                    <ASTableV2Cell>
                      <Chip
                        label={event.hub}
                        size="small"
                        variant="outlined"
                        sx={{
                          textTransform: "uppercase",
                          fontWeight: "medium",
                          backgroundColor: getHubColorStyles(event.hub)
                            .backgroundColor,
                          borderColor: getHubColorStyles(event.hub).borderColor,
                          color: getHubColorStyles(event.hub).textColor,
                          "& .MuiChip-label": {
                            color: getHubColorStyles(event.hub).textColor
                          }
                        }}
                      />
                    </ASTableV2Cell>

                    {/* Event Type */}
                    <ASTableV2Cell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={letters || "—"}
                          size="small"
                          variant="filled"
                          sx={{
                            backgroundColor: statusColor,
                            color: getEventBubbleTextColor(statusColor),
                            fontWeight: "bold",
                            fontSize: "0.6rem",
                            minWidth: 24,
                            height: 20,
                            "& .MuiChip-label": {
                              color: getEventBubbleTextColor(statusColor),
                              fontSize: "0.6rem",
                              fontWeight: "bold",
                              px: 0.5
                            }
                          }}
                        />
                        <Typography variant="body2" color="text.primary">
                          {eventType}
                        </Typography>
                      </Box>
                    </ASTableV2Cell>

                    {/* Workstation */}
                    <ASTableV2Cell>
                      <Typography variant="body2">
                        {getWorkstationDeviceName(event.workstationId || "")}
                      </Typography>
                    </ASTableV2Cell>

                    {/* Port */}
                    <ASTableV2Cell>
                      <Typography variant="body2">
                        {event.portId || "—"}
                      </Typography>
                    </ASTableV2Cell>

                    {/* Bin */}
                    <ASTableV2Cell>
                      <Typography variant="body2">
                        {event.binId || "—"}
                      </Typography>
                    </ASTableV2Cell>

                    {/* Status */}
                    <ASTableV2Cell>
                      <Box display="flex" justifyContent="center">
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: getEventStatusColor(
                              mapEventStatus(event)
                            ),
                            border: "1px solid",
                            borderColor: "divider"
                          }}
                        />
                      </Box>
                    </ASTableV2Cell>

                    {/* Details */}
                    <ASTableV2Cell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                        title={eventDetails}
                      >
                        {eventDetails}
                      </Typography>
                    </ASTableV2Cell>
                  </ASTableV2Row>
                </Fragment>
              );
            })}
          </ASTableV2Body>
        </ASTableV2>

        {paginatedEvents.length === 0 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={200}
          >
            <Typography variant="body1" color="textSecondary">
              {searchQuery
                ? t("no events match your search")
                : t("no events available")}
            </Typography>
          </Box>
        )}

        <EventsDrawer
          open={selectedEventIndex !== null}
          event={
            selectedEventIndex !== null
              ? filteredEvents[selectedEventIndex]
              : null
          }
          onClose={() => setSelectedEventIndex(null)}
        />
      </Box>
    </Stack>
  );
}

export default EventsTable;
